import { State } from '@meraki-internal/state';
import { IMedication } from '../../caremap/ICareMap';
import { CareMapState } from '../../caremap/CareMapState';
import { nanoid } from 'nanoid';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';

type IMedicationFormState = {
    medication?: IMedication;
    initialValues?: IMedication;
    usedPhoto?: boolean;
    // for tracking, determine if the user changed
    initialMedication?: IMedication;
};
export class MedicationFormState extends State<IMedicationFormState> {
    static inject = () => [
        CareMapState,
        MixPanelEventEmitter
    ];

    constructor(
        private careMapState: CareMapState,
        private mixpanel: MixPanelEventEmitter
    ) {
        super({ });
    }

    init = (id: string) => {
        const patient = this.careMapState.getActiveCaremap()?.patient;
        let medication = patient?.medications?.find(m => m.id === id);

        if (!medication) {
            medication = {
                ...{
                    id: nanoid(),
                    name: '',
                    instructions: '',
                    dose: '',
                    rxNumber: '',
                    refills: '',
                    expiration: '',
                    notes: ''
                },
                ...this.state.initialValues
            };
        }

        this.setState({ medication, initialMedication: medication });
    };

    setInitialValues = (initialValues: IMedication, usedPhoto?: boolean) => {
        this.setState({ initialValues: initialValues, usedPhoto: usedPhoto });
    };

    patch = (patch: Partial<IMedication>) => {
        if (!this.state.medication) {
            throw new Error('must call init before calling patch');
        }

        this.setState({
            medication: {
                ...this.state.medication,
                ...patch
            }
        });
    };

    delete = () => {
        if (!this.state.medication) {
            throw new Error('must call init before calling delete');
        }

        this.updateCurrentPatient(true);
    };

    updateCurrentPatient = (isDelete?: boolean) => {
        const patient = this.careMapState.getActiveCaremap()?.patient;

        if (!patient || !this.state.medication) {
            throw new Error('there is no current patient or medication to update');
        }

        let isAdd = false;
        const medication = this.state.medication;
        const copy = [ ...patient.medications || [] ];
        const idx = copy.findIndex(m => m.id === medication.id);

        if (idx > -1) {
            if (isDelete) {
                this.clear();
                copy.splice(idx, 1);
            } else {
                copy.splice(idx, 1, medication);
            }
        } else {
            isAdd = true;
            copy.push(medication);
        }

        this.careMapState.updatePatient({
            ...patient,
            ...{medications: copy}
        });

        this.track(isAdd, isDelete);
    };

    track = (isAdd: boolean, isDelete?: boolean) => {
        const medication = this.state.medication;
        if (isDelete) {
            this.mixpanel.track('Medication Removed');
        } else {
            const trackingMeta: any = {
                'Name': medication?.name,
                'Has Instructions': medication?.instructions ? true : false,
                'Has Dose': medication?.dose ? true : false,
                'Has RX #': medication?.rxNumber ? true : false,
                'Has Refills': medication?.refills ? true : false,
                'Has Expiration Date': medication?.expiration ? true : false,
                'Has Notes': medication?.notes ? true : false,
                'Type': this.state.usedPhoto ? 'photo' : 'manual'
            };

            if (isAdd && this.state.usedPhoto) {
                trackingMeta['User Changed'] = JSON.stringify(medication) !== JSON.stringify(this.state.initialMedication) ? true : false;
            }

            this.mixpanel.track(`Medication ${isAdd ? 'Added' : 'Edited'}`, () => (trackingMeta));
        }
    };

    clear = () => {
        this.setState({
            medication: undefined,
            initialValues: undefined,
            usedPhoto: undefined,
            initialMedication: undefined
        });
    };
};
